import _isFunction from 'lodash/isFunction'
import _set from 'lodash/set'
import _get from 'lodash/get'
import { Map } from 'immutable'
import {
  TYPE_CONTENT_SERIES,
  SUBTYPE_CONTENT_YOGA,
  SUBTYPE_CONTENT_FITNESS,
  SUBTYPE_CONTENT_MEDITATION,
  TYPE_CONTENT_PRODUCT,
} from '../../services/content-type/index'
import {
  SERIES_CLICK,
  TITLE_CLICK,
  PLAY_EVENT,
  PLAY_CLICK_GA4_EVENT,
  CLICK_TO_DETAILS_PAGE_GA4_EVENT,
  SPOTLIGHT_AUDIO_TOGGLE_GA4_EVENT,
  SPOTLIGHT_PLAY_TOGGLE_GA4_EVENT,
  EVENT_SPOTLIGHT_PLAY_TOGGLE,
  EVENT_SPOTLIGHT_AUDIO_TOGGLE,
} from '../../services/event-tracking/index'
import { parse as parseQuery, stringify as stringifyQuery } from '../../services/query-string/index'
import marketplaceProductUrlThroughShop from '../../services/url/marketplace'

/**
 * Handle clicks for spotlight elements
 */
export function handleClick (e, props, eventName, onClick) {
  const {
    mediaId,
    data,
    language,
    setEventDefault,
    upstreamContext,
    section = Map(),
  } = props

  // GA4 events
  const parentSeriesId = data.get('seriesId')
  const recommId = Map.isMap(section) ? section.getIn(['data', 'data', 'recommId']) : null

  // Upstream Data
  const hasValidUpstreamContext = Map.isMap(upstreamContext) && upstreamContext.size > 0
  const spotlightType = hasValidUpstreamContext ? upstreamContext.getIn(['mhTileData', 'spotlightType']) || null : null
  const rowName = hasValidUpstreamContext ? upstreamContext.getIn(['mhTileData', 'rowName']) || null : null
  const spotlightCampaign = hasValidUpstreamContext ? upstreamContext.getIn(['mhTileData', 'spotlightCampaign']) || null : null

  let ga4EventData = {
    content_id: data.get('id', 0),
    content_type: data.get('contentType'),
    content_name: data.get('title'),
    media_id: mediaId,
    media_language: data.get('mediaLanguage', language),
    is_featured_media: true,
    ui_element: 'Spotlight',
    ui_location: 'Member Home',
    row_placement: 'Spotlight',
    row_name: rowName,
    ...(recommId && { recomm_id: recommId }),
    tile_number: 0,
    click_attribution: 'Member Home',
    ...(spotlightType && { spotlight_type: spotlightType }),
    ...(spotlightCampaign && { spotlight_campaign: spotlightCampaign }),
  }

  if (parentSeriesId) {
    ga4EventData = {
      ...ga4EventData,
      parent_series_id: parentSeriesId,
      parent_series_name: data.get('seriesTitle'),
    }
  }

  if (eventName === PLAY_EVENT) {
    setEventDefault(PLAY_CLICK_GA4_EVENT.mergeDeep(ga4EventData))
  } else if (eventName === TITLE_CLICK) {
    setEventDefault(CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep(ga4EventData))
  } else if (eventName === SERIES_CLICK) {
    setEventDefault(CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep({
      content_id: data.get('seriesId'),
      content_type: TYPE_CONTENT_SERIES,
      content_name: data.get('seriesTitle'),
      is_featured_media: true,
      ui_element: 'Spotlight',
      ui_location: 'Member Home',
      row_placement: 'Spotlight',
      row_name: rowName,
      tile_number: 0,
      click_attribution: 'Member Home',
      ...(spotlightType && { spotlight_type: spotlightType }),
    }))
  }

  if (_isFunction(onClick)) {
    onClick(e)
  }
}

/**
 * Determine if the content subtype is yoga, fitness, or meditation
 * @param {String} contentSubtype - The subtype of the content
 * @returns {Boolean} Whether or not the content is subtype of yoga, fitness or meditation
 */
export function isYogaFitnessMeditation (contentSubtype) {
  return contentSubtype === SUBTYPE_CONTENT_YOGA
  || contentSubtype === SUBTYPE_CONTENT_FITNESS
  || contentSubtype === SUBTYPE_CONTENT_MEDITATION
}

/**
 * Determine if the host data should be shown
 * @param {String} contentSubtype - The subtype of the content
 * @returns {Boolean} Whether or not the host data should be shown
 */
export function shouldRenderHost (host, contentSubtype) {
  return host && isYogaFitnessMeditation(contentSubtype)
}

/**
 * Determine if the teaser data should be shown
 * @param {String} contentSubtype - The subtype of the content
 * @returns {Boolean} Whether or not the teaser data should be shown
 */
export function showTeaser (contentSubtype) {
  if (isYogaFitnessMeditation(contentSubtype)) {
    return false
  }

  return true
}

/**
 * Optimized image urls for the spotlight
 * @param {Object} props - The props
 * @param {String} props.url - The original image url
 * @returns {Object} Object with key/values for the spotlight optimized image urls
 */
export function getOptimizedSpotlightImages (props) {
  const { url } = props
  let smallImageUrl = ''
  let mediumImageUrl = ''
  let fallbackImageUrl = ''

  if (!url) {
    return {
      fallbackImageUrl,
      smallImageUrl,
      mediumImageUrl,
    }
  }

  try {
    const fallbackImageUrlObject = new URL(url)
    const imageUrlObject = new URL(url)
    const smallImageUrlObject = new URL(url)
    const mediumImageUrlObject = new URL(url)
    const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
    const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
    const fallbackImageOptions = {
      ...parsedFallbackImageQuery,
      w: '604',
      fm: 'jpg',
    }
    const smallImageOptions = {
      ...parsedImageQuery,
      w: '480',
      fm: 'webp',
      q: '95',
    }
    const mediumImageOptions = {
      ...parsedImageQuery,
      w: '768',
      fm: 'webp',
      q: '95',
    }

    const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
    const stringifiedSmallImageQuery = stringifyQuery(smallImageOptions)
    const stringifiedMediumImageQuery = stringifyQuery(mediumImageOptions)

    // set the search string back onto the fallback URL
    _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

    // set the search string back onto the image URLs
    _set(smallImageUrlObject, 'search', stringifiedSmallImageQuery)
    _set(mediumImageUrlObject, 'search', stringifiedMediumImageQuery)

    fallbackImageUrl = fallbackImageUrlObject.toString()
    smallImageUrl = smallImageUrlObject.toString()
    mediumImageUrl = mediumImageUrlObject.toString()
  } catch (e) {
    fallbackImageUrl = url
    smallImageUrl = url
    mediumImageUrl = url
  }

  return {
    fallbackImageUrl,
    smallImageUrl,
    mediumImageUrl,
  }
}

/**
 * Analytics for the Play/Pause and Mute/Unmute player buttons
 * @param {Object} options - The options
 * @param {Object} options.props - The props
 * @param {String} options.eventName - The name of the event
 * @param {Boolean} options.eventValue - The value of the event (true/false)
 */
export function handlePlayerControlButtonClick (options) {
  const { props, eventName, eventValue } = options
  const {
    data,
    mediaId,
    language,
    setEventDefault,
  } = props
  const parentSeriesId = data.get('seriesId')
  let changedTo
  let ga4EventData = {
    row_placement: 0,
    content_id: data.get('id', 0),
    content_type: data.get('contentType'),
    content_name: data.get('title'),
    media_id: mediaId,
    media_language: data.get('mediaLanguage', language),
    ui_element: 'Spotlight',
    ui_location: 'Member Home',
    changed_to: changedTo,
  }

  if (parentSeriesId) {
    ga4EventData = {
      ...ga4EventData,
      parent_series_id: parentSeriesId,
      parent_series_name: data.get('seriesTitle'),
    }
  }

  if (eventName === EVENT_SPOTLIGHT_PLAY_TOGGLE) {
    if (eventValue === true) {
      changedTo = 'play'
    } else {
      changedTo = 'pause'
    }

    ga4EventData = {
      ...ga4EventData,
      changed_to: changedTo,
    }

    setEventDefault(SPOTLIGHT_PLAY_TOGGLE_GA4_EVENT.mergeDeep(ga4EventData))
  }

  if (eventName === EVENT_SPOTLIGHT_AUDIO_TOGGLE) {
    if (eventValue === true) {
      changedTo = 'mute'
    } else {
      changedTo = 'unmute'
    }

    ga4EventData = {
      ...ga4EventData,
      changed_to: changedTo,
    }

    setEventDefault(SPOTLIGHT_AUDIO_TOGGLE_GA4_EVENT.mergeDeep(ga4EventData))
  }
}

/**
 * Get button text for the promo banner
 * @param {Map} data - The content node data
 * @param {Map} staticText - The static text
 * @returns {String | null} The button text
 */

export function getPromoButtonText (data, staticText) {
  const contentType = data.get('contentType')
  const text = data.getIn(['cta', 'label'])

  if (!text && contentType === TYPE_CONTENT_PRODUCT) {
    return staticText.get('shopNow')
  }

  return text
}

/**
 * Get button link for the promo banner
 * @param {Map} data - The content node data
 * @returns {String | null} The button link
 */

export function getPromoButtonLink (data) {
  const contentType = data.get('contentType')
  const link = data.getIn(['cta', 'path'])

  if (contentType === TYPE_CONTENT_PRODUCT) {
    return marketplaceProductUrlThroughShop(link)
  }

  return link
}
